import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from 'angular2-notifications';
import { finalize, take } from 'rxjs';
import { VehicleApiService } from 'src/app/core/services/api/vehicle-api.service';
import { ApiResponse } from 'src/app/shared/models/api-response.interface';
import { Surcharge, VehicleClass } from 'src/app/shared/models/contract.interface';
import { InputFieldType } from 'src/app/shared/models/input-types.enum';
import { VehicleBrand, VehicleModel, VehicleTrim, VehicleType } from 'src/app/shared/models/vehicle.model';

@Component({
  selector: 'app-surcharge-form',
  templateUrl: './surcharge-form.component.html',
  styleUrls: ['./surcharge-form.component.scss']
})
export class SurchargeFormComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<Surcharge>();
  @Output() onDelete = new EventEmitter<number>();
  @Output() onHide = new EventEmitter<void>();
  @Input() surchargeToEdit?: Surcharge;
  surchargeForm!: FormGroup;
  isModalVisible: boolean = false;
  typesOptions: VehicleType[] = [];
  brandsOptions: VehicleBrand[] = [];
  modelsOptions: VehicleModel[] = [];
  classesOptions: VehicleClass[] = [];
  trimsOptions: VehicleTrim[] = [];
  brandsOptionsLoading: boolean = false;
  modelsOptionsLoading: boolean = false;
  trimsOptionsLoading: boolean = false;
  protected readonly InputFieldType = InputFieldType;

  constructor(private fb: FormBuilder,
              private vehicleService: VehicleApiService,
              private notificationService: NotificationsService,) { }

  ngOnInit(): void {
    this.initForm();
    this.fetchTypes();
    this.fetchClasses();
    this.fetchBrands();
  }

  toggleModal(): void {
    if (!!this.surchargeToEdit) {
      this.patchSurchargeToEdit();
    }
    this.isModalVisible = !this.isModalVisible;
  }

  handleModalHide(): void {
    this.onHide.emit();
    this.surchargeForm.reset();
  }

  handleSurchargeSubmit(): void {
    if (this.surchargeForm.valid) {
      this.onSubmit.emit({
        ...this.surchargeForm.value,
        description: {
          en: this.surchargeForm.value.description,
        },
      });
      this.toggleModal();
    } else {
      this.notificationService.error('Error', 'Please ensure all fields are filled correctly');
    }
  }

  fetchBrands(typeId?: number): void {
    this.brandsOptionsLoading = true;
    const searchParams = !!typeId ? { filter_by_type: typeId } : { };
    this.vehicleService.getAllBrands(searchParams)
      .pipe(
        take(1),
        finalize(() => this.brandsOptionsLoading = false))
      .subscribe((result: ApiResponse) => {
        this.brandsOptions = result.data;
      });
  }

  fetchModels(brandId: number): void {
    this.modelsOptionsLoading = true;
    this.vehicleService.getModelsByBrand(brandId)
      .pipe(
        take(1),
        finalize(() => this.modelsOptionsLoading = false))
      .subscribe((result: ApiResponse) => {
        this.modelsOptions = result.data;
      });
  }

  fetchTrims(modelId: number): void {
    this.trimsOptionsLoading = true;
    this.vehicleService.getModelTrim(modelId)
      .pipe(
        take(1),
        finalize(() => this.trimsOptionsLoading = false))
      .subscribe((result: ApiResponse) => {
        this.trimsOptions = result.data;
      });
  } 

  handleSurchargeDeleting(): void {
    this.onDelete.emit(this.surchargeToEdit?.id);
  }

  private initForm(): void {
    this.surchargeForm = this.fb.group({
      surcharge: [null, Validators.required],
      amount: [null, Validators.required],
      tax_amount: [null, Validators.required],
      description: [null],
      is_active: [false, Validators.required],
      type: [null],
      brand_id: [null, Validators.required],
      model_id: [null, Validators.required],
      trim_id: [null, Validators.required],
      vehicle_class_id: [null],
      is_mandatory: [false, Validators.required],
      cylinder: [null],
      drive_type: [null, Validators.required]
    })
  }

  private fetchTypes(): void {
    this.vehicleService.getTypes()
      .pipe(take(1))
      .subscribe((result: ApiResponse) => {
        this.typesOptions = result.data;
      });
  }

  private fetchClasses(): void {
    this.vehicleService.getClasses()
      .pipe(take(1))
      .subscribe((result: ApiResponse) => {
        this.classesOptions = result.data;
      });
  }

  private patchSurchargeToEdit(): void {
    if (!this.surchargeToEdit) return;
    this.surchargeForm.patchValue({
      ...this.surchargeToEdit,
      is_active: !!this.surchargeToEdit.is_active,
      is_mandatory: !!this.surchargeToEdit.is_mandatory,
      vehicle_class_id: this.surchargeToEdit.class_id,
    });
  }
}
